import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import App from "./App.tsx";
import "./index.css";
// SAB-572: on native (Capacitor WKWebView), route navigator.geolocation through
// @capacitor/geolocation so Locance's SDK gets the native OS location prompt + coords.
// No-op on web/desktop (browser geolocation already works). Installed before render so
// it's in place before the Locance SDK ever loads.
import { installNativeGeolocationShim } from "./lib/nativeGeolocationShim";
import { installGeoPositionCache } from "./lib/geoPositionCache";
// Push deep links: routes a tapped notification to the page it is about. Registered here,
// at boot, rather than in AuthContext alongside registerAndroidPush -- that call is
// Android-only AND session-gated, and a cold-start tap arrives before login resolves.
import { registerPushTapRouting } from "./lib/native-push";
// SAB-431: initAnalytics() (Mixpanel) + trackEvent('app_opened') used to fire
// here unconditionally. Both are now gated behind hasConsent('analytics') via
// <AnalyticsGate /> mounted inside App. Sentry stays at top-level —
// classified as strictly-necessary (error tracking with PII stripped in
// beforeSend below).

// SAB-1041: injected by vite.config.ts from VERCEL_GIT_COMMIT_SHA at build time.
declare const __SENTRY_RELEASE__: string;

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.MODE,
  // The commit that produced this bundle. Without it, "when did this start?" is answered
  // by guessing at deploy times; with it, a regression names its own commit.
  release: __SENTRY_RELEASE__,
  integrations: [
    Sentry.browserTracingIntegration(),
    // Mask all text + block all media in session replays. The product
    // surfaces chat, DMs, deposit forms, and PII; capturing them verbatim
    // in error replays is a leak vector.
    Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
  ],
  tracesSampleRate: 0.1,
  replaysSessionSampleRate: 0.01,
  replaysOnErrorSampleRate: 1.0,
  enabled: import.meta.env.PROD,
  beforeSend(event) {
    // Strip auth and any header that commonly carries credentials.
    if (event.request?.headers) {
      delete event.request.headers['Authorization'];
      delete event.request.headers['authorization'];
      delete event.request.headers['Cookie'];
      delete event.request.headers['cookie'];
      delete event.request.headers['X-Api-Key'];
      delete event.request.headers['x-api-key'];
    }
    // Strip request body / cookies / query string — may contain passwords,
    // JWTs, or invite codes. Sentry can still see status / url path.
    if (event.request) {
      delete event.request.data;
      delete event.request.cookies;
      delete event.request.query_string;
    }
    // Reduce user identifiers to the opaque id only.
    if (event.user) {
      delete event.user.email;
      delete event.user.username;
      delete event.user.ip_address;
    }
    return event;
  },
});

// SW disabled — was causing "invalid state" errors and white screens.
// The inline script in index.html nukes any remaining SW registrations.

// SAB-572: install the native geolocation shim before the app renders (and long
// before the first Locance gather). No-op on web/desktop.
installNativeGeolocationShim();
// SAB item 5: observe-only cache of the browser GPS fix Locance already collects, so the
// Soap device-ping can reuse lat/long without a second prompt. Installed before render so
// it wraps getCurrentPosition before the Locance SDK loads. Web only for now.
installGeoPositionCache();
// No-op off native. Must run before render: a cold-launch tap is replayed by Capacitor to
// the FIRST listener that attaches (retainUntilConsumed), so attaching late means the tap
// is consumed by nothing and the user lands on the default route with no explanation.
void registerPushTapRouting();

// SAB (scroll-reset): opt out of the browser/webview restoring the previous scroll
// position on navigation. The ScrollToTop component (App.tsx) owns scroll reset on
// route change; the default 'auto' would fight it by restoring mid-page offsets.
if ('scrollRestoration' in history) {
  history.scrollRestoration = 'manual';
}

// SAB-657: embedded Soap checkout return relay. When Soap finishes, it redirects the
// checkout <iframe> to our same-origin return_url (…?soap_return=1). That URL loads THIS
// app inside the frame — we do not want a second full SPA booting in a tiny iframe. Detect
// the framed return, post the completion signal up to the parent (SoapCheckoutFrame catches
// it and starts the authoritative pending-row poll), and skip rendering. Same-origin framing
// is why X-Frame-Options is SAMEORIGIN (not DENY). Money truth is still the webhook.
const inEmbeddedCheckoutReturn = (() => {
  try {
    return (
      window.top !== window.self &&
      new URLSearchParams(window.location.search).get("soap_return") === "1"
    );
  } catch {
    // Cross-origin access to window.top throws only when framed by a third party — which
    // is not our own return page, so treat it as a normal (non-relay) load.
    return false;
  }
})();

if (inEmbeddedCheckoutReturn) {
  try {
    window.parent.postMessage({ type: "soap:checkout-complete" }, window.location.origin);
  } catch {
    /* parent gone / blocked — the poll + webhook still reconcile */
  }
} else {
  createRoot(document.getElementById("root")!).render(
    <Sentry.ErrorBoundary fallback={<div className="flex items-center justify-center h-screen text-center p-8"><div><h1 className="text-xl font-bold mb-2">Something went wrong</h1><p className="text-muted-foreground mb-4">An unexpected error occurred.</p><button onClick={() => window.location.reload()} className="px-4 py-2 bg-primary text-primary-foreground rounded">Reload</button></div></div>} showDialog>
      <App />
    </Sentry.ErrorBoundary>
  );
}
